<template>
  <AppLayout
    :access-status="isAuthenticated ? 'granted' : currentUserChecked ? 'blocked' : 'pending'"
    :warning-message="city?.isDemoMode ? `${city.name} is in demo mode.` : null"
    :wide-left-bar="Boolean(selectedListingId)"
    :show-detail="Boolean(selectedListingId)"
    :show-list="!selectedListingId"
    show-map
  >

    <template #top>
      <TopBar
        :city="city"
        selected-tab="listings"
        :buttons="buttons"
        :show-search="!selectedListingId"
        @search="search"
        search-placeholder="Search listings"
      />
      <div v-if="exportError || mapError" class="text-danger p-3" role="alert">
        {{ (exportError || mapError).message }}
        <button v-if="mapError" class="btn btn-link" @click="reloadLicenses">Retry map</button>
      </div>
    </template>

    <template v-if="selectedListingId" #detail>
      <ListingDetailView
        :city="city"
        :listing-id="selectedListingId"
        :panel="panel"
        @loaded="setSelectedListing"
        @update:panel="updatePanel"
        @open-street-view="openStreetView"
        @show-location="showLocation"
        @close="closeDetail"
        class="w-100"
      >
        <template #navigation>
          <IconButton
            icon="x-lg"
            title="Back to list"
            class="flex-shrink-0"
            @click="closeDetail"
          />
          <span class="text-muted small px-2">
            <template v-if="selectedResultIndex >= 0">{{ selectedResultIndex + 1 }} of {{ total }}</template>
            <template v-else>Outside current results</template>
          </span>
          <IconButton button :disabled="filtering || !total" icon="chevron-left" title="Previous listing" class="flex-shrink-0" @click="() => nextListing(true)" />
          <IconButton button :disabled="filtering || !total" icon="chevron-right" title="Next listing" class="flex-shrink-0" @click="() => nextListing(false)" />
        </template>
      </ListingDetailView>
    </template>

    <template #list>
      <FilterableList
        :count="total"
        :page-start="pageStart"
        :page-end="pageEnd"
        @previous-page="changePage(previousCursor)"
        @next-page="changePage(nextCursor)"
        @updated="applyFilterChanges"
        @canceled="cancelFilterChanges"
        :active-filter-count="activeFilterCount"
        @reset="resetFilters"
        :noun="listNoun"
        :query="query"
        :loading="loading"
        :show-header="!selectedListingId"
      >
        <template #filters v-if="draftFilters">
          <FilterEditor :filters="draftFilters" :sections="listingFilterSections">
            <slot name="filters"/>
          </FilterEditor>
        </template>
        <template #list>
          <LoadingMessage v-if="loading" message="Loading listings" />
          <LoadingMessage v-else-if="filtering" message="Updating listings" />
          <template v-else-if="listings.length === 0">
            <h6 v-if="loadError" class="text-center text-danger">
              An error occurred while loading listings: {{ String(loadError) }}
              <button class="btn btn-link" @click="city ? reloadResults() : reloadCity()">Retry</button>
            </h6>
            <slot name="empty" />
          </template>
          <ListingsList
            :listings="listings"
            :selected-id="selectedListingId"
            @select="selectListing"
          />
        </template>
      </FilterableList>
    </template>

    <template #map>
      <GMap
        ref="map"
        @deselect="selectListing(null)"
        :city="city"
        :selected-marker-id="selectedListingId"
        :items="mapItems"
        :show-property-boundaries="!!selectedListingId"
      >
        <template #marker="{item}">
          <GMapListingMarker v-if="item instanceof Listing" :listing="item" :selected-id="selectedListingId" />
          <GMapLicenseMarker v-else-if="item instanceof License" :license="item" :selected-id="null" />
        </template>
        <template #info="{data:listing}">
          <LicenseItemView v-if="listing instanceof License" :license="listing" />
          <ListingItemView v-else :value="listing" />
        </template>
        <template #top-left>
          <div class="bg-white" style="margin: 10px;border-radius:2px;box-shadow:0px 1px 3px rgba(0,0,0,0.2)">
            <button
              type="button"
              :class="`btn btn-flat ${showLicenses ? '' : 'text-muted fw-normal'}`"
              style="height:40px;line-height:40px;padding:0 10px;"
              @click="showLicenses = !showLicenses">
              <i :class="`bi-${showLicenses ? 'check-' : ''}square me-1`"/> {{ loadingMap ? 'Loading Licenses…' : 'Show Licenses' }}
            </button>
          </div>
        </template>
        <template #street-view="{address}" v-if="showAddressVerificationControls">
          <div class="m-3 p-3 rounded-2 shadow bg-white">
            <StreetViewVerificationButton
              :listing="selectedListing"
              :street-view-label="address"
            />
          </div>
        </template>
        <template #selected-feature="{feature}" v-if="showAddressVerificationControls">
          <SuggestedListingPropertyWrapper :listing="selectedListing" :property-id="getPropertyIdFromFeature(feature)" />
        </template>
      </GMap>
    </template>

  </AppLayout>
</template>

<script>
import AppLayout from '@/components/common/AppLayout.vue';
import TopBar from '@/components/common/TopBar.vue';
import GMap from '@/components/common/GMap.vue';
import ListingsList from '@/components/ListingsList.vue';
import ListingItemView from '@/components/ListingItemView.vue';
import LicenseItemView from '@/components/LicenseItemView.vue';
import ListingDetailView from '@/components/ListingDetailView.vue';
import IconButton from '@/components/common/IconButton.vue';
import FilterEditor from '@/components/common/FilterEditor.vue';
import { buildListingFilterSections } from '@/components/listings/listingFilterSections';
import GMapListingMarker from '@/components/common/GMapListingMarker.vue';
import StreetViewVerificationButton from '@/components/listing/StreetViewVerificationButton.vue';
import LoadingMessage from '@/components/common/LoadingMessage.vue';
import { Listing } from '@/models/Listing';
import { License } from '@/models/License';
import FilterableList from '@/components/common/FilterableList.vue';
import GMapLicenseMarker from '@/components/common/GMapLicenseMarker.vue';
import SuggestedListingPropertyWrapper from '@/components/SuggestedListingPropertyWrapper.vue';
import GeoJsonPropertyFeature from '@/models/GeoJsonPropertyFeature';
import { listingRepository } from '@/data';
import { createCitySearchMixin } from '@/citySearch';

const defaults = () => ({
  showUnread: false, showVerified: false, presence: 'all',
  deferred: 'not-deferred', deferralEndedWithinDays: null, source: 'all',
  verifiedByUserId: null, sort: { field: 'found', ascending: false }
});

export default {
  mixins: [createCitySearchMixin({
    itemsProperty: 'listings', selectedIdProperty: 'selectedListingId', Model: Listing,
    search: (cityId, options) => listingRepository.searchListings(cityId, options), selectMethod: 'selectListing'
  })],
  components: {
    SuggestedListingPropertyWrapper, GMapLicenseMarker, FilterableList, LoadingMessage,
    StreetViewVerificationButton, GMapListingMarker, FilterEditor, IconButton,
    ListingDetailView, ListingItemView, LicenseItemView, ListingsList, GMap, TopBar, AppLayout
  },
  props: {
    cityId: String, selectedListingId: String, panel: String, showVerifierFilter: Boolean,
    defaultShowWhitelist: Boolean, defaultShowVerified: Boolean
  },
  data() {
    return { defaultFilters: defaults(), filters: defaults(), draftFilters: null, selectedListing: null, skipAutoAdvance: false };
  },
  watch: {
    defaultShowWhitelist: { immediate: true, handler(value) { this.filters.deferred = value ? 'deferred' : 'not-deferred'; this.filterResults(); } },
    defaultShowVerified: { immediate: true, handler(value) { this.filters.showVerified = value; this.filterResults(); } },
    filters: { deep: true, handler() { this.draftFilters = { ...this.filters, sort: { ...this.filters.sort } }; } },
    selectedListingId() { this.selectedListing = null; },
    'selectedListing.verifiedPropertyId'(value, previous) {
      if (value && !previous && !this.skipAutoAdvance) this.nextListing();
    }
  },
  computed: {
    showAddressVerificationControls() { return this.selectedListing && !this.selectedListing.isPropertyVerified; },
    listingFilterSections() {
      return buildListingFilterSections({ showVerifierFilter: this.showVerifierFilter, verifiers: this.searchMetadata.verifiers || [] });
    },
    listNoun() {
      const parts = [];
      if (this.filters.deferred === 'deferred') parts.push('deferred');
      if (this.filters.deferred === 'previously-deferred') parts.push('previously deferred');
      return [...parts, 'listing'].join(' ');
    },
    Listing() { return Listing; },
    License() { return License; },
    loading() { return !this.loaded || this.filtering; },
    activeFilterCount() {
      const f = this.filters, d = this.defaultFilters;
      return ['showUnread', 'showVerified', 'deferred', 'source', 'verifiedByUserId', 'presence']
        .filter(key => f[key] !== d[key]).length
        + Number(f.sort.field !== d.sort.field || f.sort.ascending !== d.sort.ascending)
        + Number(f.deferred === 'previously-deferred' && f.deferralEndedWithinDays !== d.deferralEndedWithinDays);
    },
    buttons() {
      return this.isAuthenticated ? [{
        id: 'export', click: () => this.exportListings(), icon: 'download', showOnMobile: true,
        tooltip: this.exporting ? 'Preparing CSV export…' : 'Export all matching listings to CSV',
        text: this.exporting ? 'Exporting…' : null
      }] : [];
    },
    mapItems() {
      const items = this.filtering ? [] : [...this.listings];
      if (this.selectedListing && !items.some(item => item.id === this.selectedListing.id)) items.push(this.selectedListing);
      if (this.showLicenses) items.push(...this.licenses);
      return items;
    }
  },
  methods: {
    searchOptions() {
      const { sort, ...filters } = this.filters;
      return { ...filters, verifiedByUserId: this.showVerifierFilter ? filters.verifiedByUserId : null,
        query: this.query, sortField: sort.field, sortAscending: sort.ascending };
    },
    onSearchReset() { this.selectedListing = null; },
    setSelectedListing(listing) {
      if (listing.id !== this.selectedListingId || listing.city.id !== this.city?.id) return;
      this.skipAutoAdvance = true;
      this.selectedListing = listing;
      this.$nextTick(() => { this.skipAutoAdvance = false; });
    },
    applyFilterChanges() {
      this.filters = { ...this.draftFilters, sort: { ...this.draftFilters.sort } };
      if (this.filters.presence === 'review') {
        this.filters = { ...this.filters, showVerified: true, showUnread: false, deferred: 'all', verifiedByUserId: null };
        this.draftFilters = { ...this.filters, sort: { ...this.filters.sort } };
      }
      return this.filterResults();
    },
    cancelFilterChanges() { this.draftFilters = { ...this.filters, sort: { ...this.filters.sort } }; },
    resetFilters() { this.draftFilters = { ...this.defaultFilters, sort: { ...this.defaultFilters.sort } }; },
    nextListing(backward) { return this.nextResult(backward); },
    closeDetail() { this.selectListing(null); },
    selectListing(id) {
      this.$router.push(id
        ? { name: 'listing-detail', params: { selectedListingId: id }, query: this.$route.query }
        : { name: 'listings', query: this.$route.query });
    },
    getPropertyIdFromFeature(feature) { return GeoJsonPropertyFeature.getPropertyIdFromFeature(feature); },
    showLocation(latlng) { this.$refs.map?.setCenter(latlng, 18); },
    openStreetView(latlng) { this.$refs.map?.openStreetView(latlng); },
    updatePanel(panel) { this.$router.replace({ query: { ...this.$route.query, panel: panel || undefined } }); },
    exportListings() {
      return this.downloadExport({
        fetch: (cityId, options) => listingRepository.exportListings(cityId, options), filename: 'listings',
        headers: ['Parcel ID', 'Street Number', 'Street Name', 'Unit', 'Address', 'Owner(s)', 'License Status', 'Listing Title', 'Listing URL'],
        rows: listings => listings.sort((a, b) => (a.verifiedPropertyId || '').localeCompare(b.verifiedPropertyId || '')).map(listing => [
          listing.verifiedPropertyId, listing.verifiedProperty?.address?.streetNumber || '',
          listing.verifiedProperty?.address?.street || '', listing.verifiedProperty?.address?.unit || '',
          listing.addressFormatted, listing.verifiedProperty?.owners?.join('\n') || '', 'none', listing.listingTitle, listing.url
        ])
      });
    }
  }
};
</script>
